// client/src/pages/Dashboard.jsx
// Report Dashboard v1 (May 2026)
//
// Layout:
//   ┌──────────────────────────────────────────────────────────────────┐
//   │  4 KPI cards: presentations, quotes, POs, total quoted dollars   │
//   └──────────────────────────────────────────────────────────────────┘
//   ┌──────────────────────────────────────────────────────────────────┐
//   │  90-day activity sparkline — 3 lines: presentations/quotes/POs   │
//   └──────────────────────────────────────────────────────────────────┘
//   ┌─────────────────────────────────┬────────────────────────────────┐
//   │  Recent activity feed (left)    │  Recent clients (right)        │
//   └─────────────────────────────────┴────────────────────────────────┘
//
// Each KPI card shows "this month" + "all time" — covers both day-to-day
// pulse and the lifetime story without needing a date-range picker.
//
// Chart is inline SVG (no recharts/chart.js dependency) — keeps bundle
// small and removes one source of build risk.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

export default function Dashboard({ getToken, navigate }) {
  const [kpis,     setKpis]     = useState(null);
  const [series,   setSeries]   = useState([]);
  const [activity, setActivity] = useState([]);
  const [decisions,setDecisions]= useState([]);
  const [billing,  setBilling]  = useState(null);   // for bundle usage banner
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const a = api.withToken(token);
      const [k, s, act, dec, b] = await Promise.all([
        a.getDashboardKpis(),
        a.getDashboardSeries(90),
        a.getDashboardActivity(10),
        a.getRecentDecisions(8).catch(() => []),  // best-effort — new endpoint
        a.getBillingStatus().catch(() => null),  // best-effort
      ]);
      setKpis(k);
      setSeries(s || []);
      setActivity(act || []);
      setDecisions(dec || []);
      setBilling(b);
    } catch (e) {
      // Silent — dashboard is best-effort. New accounts get empty data and
      // see the empty states below rather than an error.
      console.error('[Dashboard] load failed:', e);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  // Distinct clients across activity, top 5 by most recent. `activity` is
  // already most-recent-first, so the first time we see a client is their
  // latest touchpoint — capture its client_id (and type/ref_id as a
  // fallback) then, not on later hits. `types` still accumulates every
  // kind seen, purely for the "pres · po · quote" label.
  const recentClients = useMemo(() => {
    const seen = new Map();
    for (const a of activity) {
      if (!a.client) continue;
      if (!seen.has(a.client)) {
        seen.set(a.client, {
          name: a.client,
          last: a.created_at,
          types: new Set([a.type]),
          clientId: a.client_id || null,
          lastType: a.type,
          lastRefId: a.ref_id,
        });
      } else {
        seen.get(a.client).types.add(a.type);
      }
    }
    return [...seen.values()].slice(0, 5);
  }, [activity]);

  return (
    <div style={{ padding:24 }}>
      <div style={{ fontSize:22, fontWeight:600, color:BRAND.colors.dark, marginBottom:6 }}>Dashboard</div>
      <div style={{ fontSize:13, color:'#64748B', marginBottom:24 }}>Your activity at a glance</div>

      <BundleUsageBanner billing={billing} navigate={navigate}/>

      <KpiRow kpis={kpis} loading={loading}/>

      <div style={{ background:'#fff', border:'1px solid #E2E8F0', borderRadius:12, padding:24, marginTop:18 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:14 }}>
          <div style={{ fontSize:14, fontWeight:600, color:BRAND.colors.dark }}>Last 90 days</div>
          <ChartLegend/>
        </div>
        <ActivityChart series={series} loading={loading}/>
      </div>

      <ClientDecisionsCard decisions={decisions} loading={loading} navigate={navigate}/>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:18, marginTop:18 }}>
        <RecentActivity activity={activity} loading={loading} navigate={navigate}/>
        <RecentClients clients={recentClients} navigate={navigate}/>
      </div>
    </div>
  );
}

// ─── Client decisions card ───────────────────────────────────────────────────
// Surfaces approvals and change-requests on presentations — the two client
// actions that previously only showed up as a small badge buried in the
// Presentations list, with no notification at all. Approved presentations
// are the "good news, act on it" case (build a PO); change requests carry
// the client's actual typed feedback, shown right in the card so a rep
// doesn't have to go hunting for what was actually asked for.
function ClientDecisionsCard({ decisions, loading, navigate }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #E2E8F0', borderRadius:12, padding:20, marginTop:18 }}>
      <div style={{ fontSize:14, fontWeight:600, color:BRAND.colors.dark, marginBottom:14 }}>Client decisions</div>
      {loading ? (
        <div style={{ fontSize:13, color:'#94A3B8' }}>Loading…</div>
      ) : decisions.length === 0 ? (
        <div style={{ fontSize:13, color:'#94A3B8' }}>No approvals or change requests yet — they'll show up here as soon as a client responds to a shared presentation.</div>
      ) : (
        decisions.map(d => {
          const isApproved = d.status === 'approved';
          return (
            <div key={d.id} onClick={() => {
                // Same per-id hand-off as ActivityRow/ClientDetailPage — no
                // direct presentation route exists, so stash the id for
                // Presentations.jsx's mount effect to pick up.
                try { sessionStorage.setItem('pending_presentation_open', JSON.stringify({ id: d.id })); } catch {}
                navigate('presentations');
              }}
              style={{
                display:'flex', alignItems:'flex-start', gap:12, padding:'10px 12px', marginBottom:8,
                borderRadius:8, cursor:'pointer',
                background: isApproved ? `${G}0D` : '#FEF3C7',
                borderLeft: `3px solid ${isApproved ? G : '#F59E0B'}`,
              }}>
              <div style={{ fontSize:18, lineHeight:1, marginTop:1 }}>{isApproved ? '✓' : '✎'}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, color:'#0F172A' }}>
                  <strong>{d.client_name || 'A client'}</strong> {isApproved ? 'approved' : 'requested changes on'}{' '}
                  <strong>{d.name || 'a presentation'}</strong>
                </div>
                {!isApproved && d.changes_message && (
                  <div style={{ fontSize:12, color:'#78716C', marginTop:4, fontStyle:'italic',
                    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    "{d.changes_message}"
                  </div>
                )}
                <div style={{ fontSize:11, color:'#94A3B8', marginTop:3 }}>{timeAgo(d.decided_at)}</div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// Minimal relative-time formatter — "3h ago", "2d ago", falls back to a date
// once it's more than a couple weeks old.
function timeAgo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 14) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ─── AI bundle usage banner ────────────────────────────────────────────────
// Shows the distributor's current AI Bundle Builder usage as X / Y with a
// progress bar. Escalates color from green → amber → red as quota fills.
// Always visible — even at 0/100 — so distributors learn the limit exists
// before hitting it. Clicking takes them to Billing for upgrade options.
function BundleUsageBanner({ billing, navigate }) {
  if (!billing) return null;
  const used  = billing.bundleSearchesUsed ?? 0;
  const quota = billing.bundleSearchesQuota ?? 0;
  if (quota === 0) return null;

  const pct = Math.min(100, (used / quota) * 100);
  // Color tiers:
  //   <50%  → green (plenty of room)
  //   50-79 → green still, but messaging shifts
  //   80-99 → amber warning
  //   100   → red, quota hit, hard upgrade prompt
  const isLimit  = used >= quota;
  const isHigh   = pct >= 80;
  const barColor = isLimit ? '#EF4444' : isHigh ? '#F59E0B' : '#10b981';
  const bgColor  = isLimit ? '#FEE2E2' : isHigh ? '#FEF3C7' : '#F0FDF4';
  const borderColor = isLimit ? '#FCA5A5' : isHigh ? '#FCD34D' : '#A7F3D0';
  const textColor   = isLimit ? '#991B1B' : isHigh ? '#92400E' : '#065F46';

  const message = isLimit
    ? "You've hit your AI Bundle Builder limit for this billing period."
    : isHigh
      ? "You're approaching your AI Bundle Builder limit."
      : null;

  return (
    <div onClick={() => navigate?.('billing')} style={{
      background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 10,
      padding: '12px 16px', marginBottom: 18, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    }}>
      <div style={{ fontSize: 18 }}>🤖</div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: textColor }}>
            AI Bundle searches: {used} / {quota}
          </span>
          {message && <span style={{ fontSize: 11, color: textColor }}>{message}</span>}
        </div>
        <div style={{ height: 6, background: 'rgba(15,23,42,0.06)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: barColor, transition: 'width 0.3s' }}/>
        </div>
      </div>
      <div style={{ fontSize: 12, color: textColor, fontWeight: 500, textDecoration: 'underline' }}>
        {isLimit || isHigh ? 'Upgrade →' : 'Manage plan →'}
      </div>
    </div>
  );
}

// ─── KPI cards ──────────────────────────────────────────────────────────────
function KpiRow({ kpis, loading }) {
  const cards = [
    { key:'pres',   label:'Presentations', month: kpis?.month_presentations, total: kpis?.total_presentations, suffix:null },
    { key:'quotes', label:'Quote Requests', month: kpis?.month_quotes, total: kpis?.total_quotes, suffix: kpis?.open_quotes != null ? `${kpis.open_quotes} open` : null },
    { key:'pos',    label:'Purchase Orders', month: kpis?.month_pos, total: kpis?.total_pos, suffix:null },
    { key:'dollars',label:'Quoted Volume', month: kpis?.month_quoted_dollars, total: kpis?.total_quoted_dollars, dollar: true, suffix:null },
  ];
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
      {cards.map(c => (
        <div key={c.key} style={{ background:'#fff', borderRadius:12, padding:'18px 20px', border:'1px solid #E2E8F0' }}>
          <div style={{ fontSize:11, color:'#94A3B8', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:8 }}>{c.label}</div>
          <div style={{ fontSize:26, fontWeight:600, color:BRAND.colors.dark, letterSpacing:'-0.5px' }}>
            {loading ? '—' : fmtNum(c.month, c.dollar)}
          </div>
          <div style={{ fontSize:11, color:'#94A3B8', marginTop:4 }}>this month</div>
          <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid #F1F5F9', display:'flex', justifyContent:'space-between', fontSize:11 }}>
            <span style={{ color:'#94A3B8' }}>All time</span>
            <span style={{ color:'#475569', fontWeight:500 }}>{loading ? '—' : fmtNum(c.total, c.dollar)}</span>
          </div>
          {c.suffix && (
            <div style={{ marginTop:6, fontSize:11, color:G, fontWeight:500 }}>{c.suffix}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function fmtNum(n, dollar) {
  if (n == null) return '0';
  const num = parseFloat(n);
  if (dollar) {
    if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'k';
    return '$' + num.toFixed(0);
  }
  return num.toLocaleString();
}

// ─── 90-day chart (inline SVG) ──────────────────────────────────────────────
function ChartLegend() {
  return (
    <div style={{ display:'flex', gap:14, fontSize:11 }}>
      <LegendDot color={G}        label="Presentations"/>
      <LegendDot color="#3B82F6"  label="Quotes"/>
      <LegendDot color="#F59E0B"  label="POs"/>
    </div>
  );
}
function LegendDot({ color, label }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, color:'#64748B' }}>
      <span style={{ width:8, height:8, borderRadius:'50%', background:color }}/>
      {label}
    </span>
  );
}

function ActivityChart({ series, loading }) {
  // Empty state: no rows at all from server (shouldn't happen — generate_series
  // always returns N rows). But guard anyway.
  if (loading) return <div style={{ height:160, display:'flex', alignItems:'center', justifyContent:'center', color:'#94A3B8', fontSize:12 }}>Loading…</div>;
  if (!series.length) return <div style={{ height:160, display:'flex', alignItems:'center', justifyContent:'center', color:'#94A3B8', fontSize:12 }}>No data yet — your activity will appear here over time.</div>;

  const W = 760;
  const H = 160;
  const padL = 32, padR = 8, padT = 8, padB = 22;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  // Max across all three series for shared Y axis. Floor at 1 so zero-data
  // doesn't divide-by-zero.
  const maxY = Math.max(
    1,
    ...series.map(d => Math.max(d.presentations, d.quotes, d.pos))
  );

  const xAt = (i) => padL + (i / Math.max(series.length - 1, 1)) * plotW;
  const yAt = (v) => padT + plotH - (v / maxY) * plotH;

  const linePath = (key) => {
    return series.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(d[key])}`).join(' ');
  };

  // Pick ~5 x-axis labels (first, last, ~25%, 50%, 75%).
  const tickIdxs = series.length <= 5
    ? series.map((_, i) => i)
    : [0, Math.floor(series.length * 0.25), Math.floor(series.length * 0.5), Math.floor(series.length * 0.75), series.length - 1];

  const fmtTickDate = (d) => {
    const dt = new Date(d);
    return `${dt.getMonth()+1}/${dt.getDate()}`;
  };

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:'block' }} preserveAspectRatio="xMidYMid meet">
      {/* Y gridlines (4) + labels */}
      {[0, 0.25, 0.5, 0.75, 1].map(p => {
        const y = padT + plotH * (1 - p);
        const val = Math.round(maxY * p);
        return (
          <g key={p}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#F1F5F9" strokeWidth={1}/>
            <text x={padL - 6} y={y + 3} fontSize="9" fill="#94A3B8" textAnchor="end">{val}</text>
          </g>
        );
      })}
      {/* X-axis tick labels */}
      {tickIdxs.map(i => (
        <text key={i} x={xAt(i)} y={H - 6} fontSize="9" fill="#94A3B8" textAnchor="middle">
          {fmtTickDate(series[i].date)}
        </text>
      ))}
      {/* Lines */}
      <path d={linePath('presentations')} fill="none" stroke={G}        strokeWidth={2}/>
      <path d={linePath('quotes')}        fill="none" stroke="#3B82F6"  strokeWidth={2}/>
      <path d={linePath('pos')}           fill="none" stroke="#F59E0B"  strokeWidth={2}/>
    </svg>
  );
}

// ─── Recent activity feed ──────────────────────────────────────────────────
function RecentActivity({ activity, loading, navigate }) {
  if (loading) return <Card title="Recent activity"><Empty>Loading…</Empty></Card>;
  if (!activity.length) return (
    <Card title="Recent activity">
      <Empty>No activity yet. Your presentations, quote requests, and POs will appear here.</Empty>
    </Card>
  );

  return (
    <Card title="Recent activity">
      {activity.map(a => <ActivityRow key={`${a.type}-${a.ref_id}`} a={a} navigate={navigate}/>)}
    </Card>
  );
}

function ActivityRow({ a, navigate }) {
  const icon  = a.type === 'presentation' ? '📋' : a.type === 'quote' ? '📬' : '📦';
  const verb  = a.type === 'presentation' ? 'Presentation' : a.type === 'quote' ? 'Quote request' : 'PO';
  const time  = relativeTime(a.created_at);

  // POs have a real per-id page. Presentations don't, so hand the id off via
  // sessionStorage the same way ClientDetailPage does — Presentations.jsx's
  // pending_presentation_open effect picks it up and opens the editor
  // directly. Quotes have no per-id page at all (confirmed: QuotesPage is a
  // flat list, no subroute), so the best honest fix is landing pre-filtered
  // to this client's name via QuotesPage's existing search box, rather than
  // dumping the user on the full unfiltered list.
  const onOpen = () => {
    if (a.type === 'po') {
      navigate(`pos/${a.ref_id}`);
    } else if (a.type === 'presentation') {
      try { sessionStorage.setItem('pending_presentation_open', JSON.stringify({ id: a.ref_id })); } catch {}
      navigate('presentations');
    } else {
      try { sessionStorage.setItem('pending_quotes_search', a.client || ''); } catch {}
      navigate('quotes');
    }
  };

  const statusBadge = (() => {
    const s = a.status;
    if (a.type === 'presentation') {
      if (s === 'approved') return { label: 'Approved', color: G, bg: `${G}18` };
      if (s === 'sent')     return { label: 'Sent',     color: '#3B82F6', bg: '#DBEAFE' };
      if (s === 'draft')    return { label: 'Draft',    color: '#94A3B8', bg: '#F1F5F9' };
      if (s === 'changes_requested') return { label: 'Changes requested', color: '#F59E0B', bg: '#FEF3C7' };
    }
    if (a.type === 'quote') {
      if (s === 'new')         return { label: 'New',         color: G, bg: `${G}18` };
      if (s === 'in_progress') return { label: 'In progress', color: '#F59E0B', bg: '#FEF3C7' };
      if (s === 'quoted')      return { label: 'Quoted',      color: '#3B82F6', bg: '#DBEAFE' };
    }
    if (a.type === 'po') {
      if (s === 'sent')   return { label: 'Sent',   color: '#3B82F6', bg: '#DBEAFE' };
      if (s === 'draft')  return { label: 'Draft',  color: '#94A3B8', bg: '#F1F5F9' };
      if (s === 'closed') return { label: 'Closed', color: '#64748B', bg: '#F1F5F9' };
    }
    return null;
  })();

  return (
    <div onClick={onOpen}
      style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 4px', borderBottom:'1px solid #F1F5F9', cursor:'pointer' }}>
      <span style={{ fontSize:18 }}>{icon}</span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, color:BRAND.colors.dark, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {verb}: {a.title || 'Untitled'}
        </div>
        <div style={{ fontSize:11, color:'#94A3B8', marginTop:2 }}>
          {a.client && <>{a.client} · </>}
          {time}
          {a.amount != null && a.amount > 0 && <> · ${parseFloat(a.amount).toFixed(2)}</>}
        </div>
      </div>
      {statusBadge && (
        <span style={{ background:statusBadge.bg, color:statusBadge.color, fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:10, flexShrink:0 }}>
          {statusBadge.label}
        </span>
      )}
    </div>
  );
}

function relativeTime(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const min = ms / 60000;
  if (min < 1) return 'just now';
  if (min < 60) return `${Math.floor(min)}m ago`;
  const hr = min / 60;
  if (hr < 24) return `${Math.floor(hr)}h ago`;
  const d = hr / 24;
  if (d < 7) return `${Math.floor(d)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ─── Recent clients ────────────────────────────────────────────────────────
function RecentClients({ clients, navigate }) {
  if (!clients.length) return null;
  return (
    <Card title="Recent clients">
      {clients.map((c, i) => {
        // Prefer the client's own page — it shows everything (all
        // presentations/POs/quotes), which is more useful than any single
        // record. Only fall back to opening one specific record when this
        // client has no linked client_id yet (older rows predate client
        // linking and only ever stored a name snapshot).
        const goTo = c.clientId
          ? `clients/${c.clientId}`
          : c.lastType === 'po' ? `pos/${c.lastRefId}`
          : c.lastType === 'quote' ? 'quotes'
          : 'presentations';
        return (
        <div key={i} onClick={() => navigate(goTo)}
          style={{ padding:'10px 4px', borderBottom:'1px solid #F1F5F9', cursor:'pointer' }}>
          <div style={{ fontSize:13, color:BRAND.colors.dark, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {c.name}
          </div>
          <div style={{ fontSize:11, color:'#94A3B8', marginTop:2 }}>
            {[...c.types].map(t => t === 'presentation' ? 'pres' : t === 'quote' ? 'quote' : 'po').join(' · ')}
            {' · '}{relativeTime(c.last)}
          </div>
        </div>
        );
      })}
    </Card>
  );
}

// ─── Shared card wrapper ────────────────────────────────────────────────────
function Card({ title, children }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #E2E8F0', borderRadius:12, padding:20 }}>
      <div style={{ fontSize:13, fontWeight:600, color:BRAND.colors.dark, marginBottom:12 }}>{title}</div>
      {children}
    </div>
  );
}
function Empty({ children }) {
  return <div style={{ fontSize:12, color:'#94A3B8', textAlign:'center', padding:'18px 0' }}>{children}</div>;
}
